import FilterListIcon from '@mui/icons-material/FilterList';
import { Divider, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ClientDTO } from 'wisestep-api';
import {
  WsAutoComplete,
  WsButton,
  WsColors,
  SearchBarNew,
  WsColorsNew,
  WsBadge,
  WsBadgetype,
  WsBadgeSize,
  formatDateToMonthDayYearString,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../components/ClientAutocomplete';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { getFilterValue } from '../../../features/notes/helper/NotesHelper';
import SmartFilter from '../../../features/submissionsNew/components/filters/smart/SmartFilter';
import { IFilters, contactsDefaultFilterValues } from '../helper/ContactListingHelper';

export interface IContactsSmartFilters {
  searchQuery: IFilters;
  getUpdatedFiltersFromFilters: (data: IFilters) => void;
  onAdvancedFiltersClick: () => void;
  onAddButtonClick?: () => void;
  isClientContactTab?: boolean;
  onSubmit: (fields: IFilters) => void;
  advFilterApply: IFilters;
}

const ContactsSmartFilters = ({
  searchQuery,
  getUpdatedFiltersFromFilters,
  onAdvancedFiltersClick,
  isClientContactTab = false,
  onSubmit,
  advFilterApply,
}: IContactsSmartFilters) => {
  const methods = useForm<IFilters>({
    defaultValues: searchQuery ?? contactsDefaultFilterValues,
  });
  const { getValues, setValue, watch, reset } = methods;
  const onSearch = () => handleFilters();
  const [applyedClientContactStatus, setApplyedClientContactStatus] = useState<string[]>(
    getValues('status') ?? []
  );
  const [applyedClientFilter, setApplyedClientFilter] = useState<ClientDTO[]>(
    searchQuery?.clients ?? []
  );
  const [applyedContactOwners, setApplyedContactOwners] = useState<string[]>(
    searchQuery?.contactOwners ?? []
  );

  useEffect(() => {
    setApplyedClientContactStatus(
      searchQuery?.status?.length ? searchQuery?.status : getValues('status') ?? []
    );
    setApplyedClientFilter(searchQuery?.clients ?? []);
    setApplyedContactOwners(searchQuery?.contactOwners ?? []);
  }, [searchQuery]);

  const handleFilters = useCallback(() => {
    const formValues = getValues();
    const updatedQuery = {
      ...searchQuery,
      ...formValues,
    };
    getUpdatedFiltersFromFilters(updatedQuery);
  }, [searchQuery, getUpdatedFiltersFromFilters, getValues]);

  const handleClear = (fieldName: keyof IFilters) => {
    setValue(fieldName, contactsDefaultFilterValues[fieldName]);
    onSubmit({ ...getValues(), [fieldName]: contactsDefaultFilterValues[fieldName] });
  };
  const handleClearAll = () => {
    reset(contactsDefaultFilterValues);
    onSearch();
  };

  const handleStatusClear = (label: string) => onFilterClear('status', label);

  const handleClientClear = (label: string) => {
    const values = getValues();
    const updatedStatus: ClientDTO[] | undefined =
      values.clients && Array.isArray(values.clients)
        ? values.clients.filter((item) => item.id !== label)
        : undefined;
    setValue('clients', updatedStatus ?? []);
    onSearch();
  };
  const handleTagClear = (label: string) => onFilterClear('tags', label);

  const onCreatedOrUpdatedBetweenClear = () => {
    setValue('filterByDateType', '');
    setValue('fromDate', '');
    setValue('toDate', '');
    onSearch();
  };
  const handleCreatedByClear = (value: string) => onFilterClear('createdByIds', value);

  const handleSupervisorClear = (value: string) => onFilterClear('supervisors', value);

  const handleOwnerClear = (value: string) => onFilterClear('contactOwners', value);

  const onClearClientLocation = () => setValue('location', undefined);

  const onFilterClear = (
    field: 'createdByIds' | 'supervisors' | 'tags' | 'contactOwners' | 'status',
    value: string
  ) => {
    const values = getValues();
    const fieldValues = values[field];
    const updatedValues: string[] | undefined =
      fieldValues && Array.isArray(fieldValues)
        ? fieldValues.filter((item) => item !== value)
        : undefined;
    setValue(field, updatedValues);
    onSearch();
  };

  const hasValue = (val: IFilters): boolean => {
    if (Array.isArray(val)) return val.length > 0;
    if (val && typeof val === 'object') return Object.values(val).some(hasValue);
    return Boolean(val);
  };

  const hasFilterValue = (): boolean => {
    const hasContactStatus = (applyedClientContactStatus?.length ?? 0) > 0;
    const hasClientFilter = !!applyedClientFilter?.[0]?.value;
    const hasContactOwners = (applyedContactOwners?.length ?? 0) > 0;
    const advFilterCheck = hasValue(advFilterApply);

    return hasContactStatus || hasClientFilter || hasContactOwners || advFilterCheck;
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSearch)}>
        <Grid container bgcolor={WsColors.White}>
          <Grid item xs={4} sx={{ my: 2, ml: 1 }}>
            <SearchBarNew size="small" onSearch={onSearch} name="search" />
          </Grid>
          <Grid item container justifyContent="flex-start" alignItems="center" xs={5} spacing={1}>
            <Grid item>
              <SmartFilter
                filterName="Contact Owner"
                onClick={onSearch}
                onClear={() => handleClear('contactOwners')}
                filterValue={getFilterValue('contactOwners', methods)}
              >
                <RecruiterSuggestions
                  name="contactOwners"
                  multiple
                  value={watch('contactOwners')}
                  onSelectMultipleValues={(val) => setValue('contactOwners', val)}
                />
              </SmartFilter>
            </Grid>
            <Grid item>
              <SmartFilter
                filterName="Contact Status"
                onClick={onSearch}
                onClear={() => handleClear('status')}
                filterValue={getFilterValue('status', methods)}
              >
                <WsAutoComplete
                  onTagsChange={(val) => setValue('status', val)}
                  placeholder="Select Status"
                  initialValue={watch('status') ?? []}
                  optionsInStringArray={['All', 'Active', 'Inactive']}
                  value={watch('status') ?? []}
                  multiple
                  freeSolo
                  name="status"
                  size="small"
                />
              </SmartFilter>
            </Grid>
            {!isClientContactTab && (
              <Grid item>
                <SmartFilter
                  filterName="Client"
                  onClick={onSearch}
                  onClear={() => handleClear('clients')}
                  filterValue={getFilterValue('clients', methods)}
                >
                  <ClientAutocomplete name="clients" multiple />
                </SmartFilter>
              </Grid>
            )}
          </Grid>
          <Grid item container justifyContent="flex-end" xs={2} alignItems="center" sx={{ ml: 9 }}>
            <Grid item>
              <WsButton
                variant="outlined"
                size="medium"
                onClick={onAdvancedFiltersClick}
                startIcon={<FilterListIcon />}
              >
                Advanced filters
              </WsButton>
            </Grid>
          </Grid>

          <Grid container pt={2} spacing={1} style={{ display: 'flex', alignItems: 'center' }}>
            {applyedClientContactStatus?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Status: ${label}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => handleStatusClear(label)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}

            {applyedClientFilter?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Client: ${label.name}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => handleClientClear(label.id)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}

            {applyedContactOwners?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Owner: ${label}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => handleOwnerClear(label)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}

            {advFilterApply?.filterByDateType && (
              <Grid item>
                <WsBadge
                  label={`${
                    advFilterApply.filterByDateType === 'CREATED_BETWEEN' ? 'Created' : 'Updated'
                  } Between: ${formatDateToMonthDayYearString(
                    new Date(advFilterApply?.fromDate ?? '')
                  )} - ${formatDateToMonthDayYearString(new Date(advFilterApply?.toDate ?? ''))}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => onCreatedOrUpdatedBetweenClear?.()}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            )}
            {advFilterApply?.createdByIds?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Created By: ${label}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => handleCreatedByClear(label)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}
            {advFilterApply?.location && (
              <Grid item>
                <WsBadge
                  label={`Location: ${[
                    advFilterApply?.location.city,
                    advFilterApply?.location.state,
                    advFilterApply?.location.country,
                  ]
                    .filter(Boolean)
                    .join(', ')}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => onClearClientLocation?.()}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            )}
            {advFilterApply?.supervisors?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Supervisor: ${label}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => handleSupervisorClear(label)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}
            {advFilterApply?.tags?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Tag: ${label}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => handleTagClear(label)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}

            {hasFilterValue() && (
              <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    backgroundColor: WsColorsNew.neutral_400a,
                    height: 30,
                    mx: 0.5,
                  }}
                />
                <Grid item pl={0.5}>
                  <WsButton onClick={handleClearAll} variant="text">
                    Clear Filters
                  </WsButton>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default ContactsSmartFilters;
